import { formatDateValue } from "../../components/DataTable";
import type { JsonRecord } from "../../types";

export type CompactacionSondeo = {
  id: string;
  informe_id: string;
  sondeo?: string | number | null;
  x_sondeo?: number | null;
  y_sondeo?: number | null;
  masa_lugar?: number | null;
  grado_reportado?: number | null;
  grado_calculado?: number | null;
  dictamen?: string | null;
  comentarios?: string | null;
};

function valueText(value: unknown) {
  if (value == null || value === "") return "—";
  return formatDateValue(value) || String(value);
}

export function formatSondeoUbicacion(sondeo: CompactacionSondeo) {
  const x = sondeo.x_sondeo == null ? "" : String(sondeo.x_sondeo);
  const y = sondeo.y_sondeo == null ? "" : String(sondeo.y_sondeo);
  if (x && y) return `X: ${x} · Y: ${y}`;
  if (x) return `X: ${x}`;
  if (y) return `Y: ${y}`;
  return "—";
}

function DetailItem({ label, value, className = "" }: { label: React.ReactNode; value: unknown; className?: string }) {
  return <div className={className}><span>{label}</span><strong>{valueText(value)}</strong></div>;
}

function SoundingsTable({ rows, onEdit }: { rows: CompactacionSondeo[]; onEdit?: (row: CompactacionSondeo) => void }) {
  if (!rows.length) return <div className="result-detail-wide"><span>Sondeos</span><strong>Sin sondeos asociados.</strong></div>;
  return <div className="result-detail-wide result-soundings-wrap">
    <span>Sondeos asociados</span>
    <div className="table-wrap">
      <table className="result-soundings-table">
        <thead><tr>
          <th>Sondeo</th><th>Ubicación</th><th>MVS del lugar (kg/m³)</th><th>Grado reportado (%)</th><th>Grado calculado (%)</th><th>Dictamen</th><th>Comentarios</th>{onEdit && <th>Acciones</th>}
        </tr></thead>
        <tbody>{rows.map((row, index) => {
          return <tr key={String(row.id || index)}>
            <td>{valueText(row.sondeo)}</td><td>{formatSondeoUbicacion(row)}</td><td>{valueText(row.masa_lugar)}</td><td>{valueText(row.grado_reportado)}</td><td>{valueText(row.grado_calculado)}</td><td>{valueText(row.dictamen)}</td><td>{valueText(row.comentarios)}</td>
            {onEdit && <td><button className="button ghost sounding-edit-button" onClick={() => onEdit(row)}>Editar</button></td>}
          </tr>;
        })}</tbody>
      </table>
    </div>
  </div>;
}

export function SpecialResultDetails({ typeKey, row, laboratory, onEditSounding }: { typeKey: string; row: JsonRecord; laboratory: string; onEditSounding?: (row: CompactacionSondeo) => void }) {
  if (typeKey === "compactacion_suelos") {
    const soundings = Array.isArray(row.sondeos) ? row.sondeos as CompactacionSondeo[] : [];
    return <>
      <DetailItem className="compaction-detail-quarter" label="Especificación (%)" value={row.especificacion} />
      <DetailItem className="compaction-detail-quarter" label="Tolerancia (%)" value={row.tolerancia == null || row.tolerancia === "" ? null : `± ${row.tolerancia}`} />
      <DetailItem className="compaction-detail-quarter" label="MVSM (kg/m³)" value={row.mvsm} />
      <DetailItem className="compaction-detail-quarter" label="Fecha informe" value={row.fecha_informe} />
      <DetailItem className="compaction-detail-laboratory" label="Laboratorio" value={laboratory} />
      <DetailItem className="compaction-detail-comments" label="Comentarios" value={row.comentarios} />
      <SoundingsTable rows={soundings} onEdit={onEditSounding} />
    </>;
  }

  if (typeKey === "estructuras_soldadura") {
    return <>
      <DetailItem label="Fecha informe" value={row.fecha_informe} />
      {row.laboratorio_id ? <DetailItem label="Laboratorio" value={laboratory} /> : null}
      {row.inspector_id ? <DetailItem label="Inspector" value={row.inspector_nombre || row.nombre_completo} /> : null}
      {row.equipo_id ? <DetailItem label="Equipo" value={row.equipo_nombre} /> : null}
      <DetailItem label="Comentarios" value={row.comentarios} />
    </>;
  }

  if (typeKey === "petreos_balasto") {
    return <>
      <DetailItem label="Estudio" value={row.entregable} />
      <DetailItem label="Laboratorio" value={laboratory} />
      <DetailItem label="Lugar muestreo" value={row.lugar_muestreo} />
      <DetailItem label="Fecha informe" value={row.fecha_informe} />
      <DetailItem label="Especificación" value={row.especificacion} />
      <DetailItem label="Lote" value={row.lote} />
      <DetailItem label="Densidad" value={row.densidad} />
      <DetailItem label="% Absorción" value={row.absorcion} />
      <DetailItem label="% Partículas deleznables" value={row.particulas_deleznables} />
      <DetailItem label="% Desgaste LA" value={row.desgaste} />
      <DetailItem label={<><span>% Intemperismo en Na</span><sub>2</sub><span>SO</span><sub>4</sub></>} value={row.intemperismo_sodio} />
      <DetailItem label="% Partículas planas y alargadas" value={row.elementos_aciculares_lajosos} />
      <DetailItem label="% Espesores entre 25 y 16 mm" value={row.em_25_16} />
      <DetailItem label="% Espesores < 16 mm" value={row.em_16} />
      <DetailItem label="% Elementos >= 100 mm" value={row.longitud_100} />
      <DetailItem label="% Pasa 0.075 mm" value={row.pasa_0075} />
      <DetailItem label="% Pasa 0.063 mm (vía húmeda)" value={row.pasa_0063} />
      <DetailItem label="Comentarios" value={row.comentarios} />
    </>;
  }

  if (typeKey === "pedraplen") {
    return <>
      <DetailItem label="Estudio" value={row.entregable} />
      <DetailItem label="Laboratorio" value={laboratory} />
      <DetailItem label="Fecha de informe" value={row.fecha_informe} />
      <DetailItem label="Lugar" value={row.lugar_muestreo} />
      <DetailItem label="Tamaño máximo" value={row.t_max} />
      <DetailItem label="MVM (kg/m³)" value={row.densidad} />
      <DetailItem label="L. Max / L. Min" value={row.rel_lmax_lmin} />
      <DetailItem label="Intemperismo en Na₂SO₄ (%)" value={row.intemp_sodio} />
      <DetailItem label="Intemperismo en MgSO₄ (%)" value={row.intemp_magnesio} />
      <DetailItem label="% mayor que 75 cm" value={row.pasa_750} />
      <DetailItem label="% mayor que 20 cm" value={row.pasa_200} />
      <DetailItem label="% menor que 7.5 cm" value={row.pasa_75} />
      <DetailItem label="% menor que D" value={row.pasa_d} />
      <DetailItem label="% menor que D/2" value={row.pasa_d2} />
      <DetailItem label="% menor que D/4" value={row.pasa_d4} />
      <DetailItem label="% menor que D/10" value={row.pasa_d10} />
      <DetailItem label="Comentarios" value={row.comentarios} />
    </>;
  }

  return null;
}
